import classNames from "classnames";
import CiroBreadCrumbs from "../../components/shared/CiroBreadCrumbs";
import CiroContainer from "../../components/shared/CiroContainer";
import {
  EnumPersonaSource,
  InternalAutopilotCuratedSearch_AutopilotCuratedSearchQuery,
  InternalAutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables,
} from "../../__generated__/graphql";
import { gql, useQuery } from "@apollo/client";
import AutopilotCuratedSearchContext, {
  AutopilotCuratedSearchTypeEnum,
} from "../../contexts/AutopilotCuratedSearchContext";
import { useContext, useMemo, useState } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import CuratedSearchCriteriaAndFiltersContainer from "../../components/autopilotCuratedSearch/CuratedSearchCriteriaAndFiltersContainer";
import AutopilotCuratedInternalSearchContext from "../../contexts/AutopilotCuratedInternalSearchContext";
import InternalAutopilotHydrationContext from "../../contexts/InternalAutopilotHydrationContext";
import { AutopilotProvider } from "../../components/contexts/AutopilotProvider";
import CuratedSearchBuilderHeading from "../../components/autopilotCuratedSearch/CuratedSearchPrompt/CuratedSearchBuilderHeading";
import CapacityLimitMessage from "../../components/shared/CapacityLimitMessage";

const InternalAutopilotCuratedSearch_AutopilotCuratedSearch = gql`
  query InternalAutopilotCuratedSearch_AutopilotCuratedSearch {
    personas {
      id
      name
      description
      source
    }
  }
`;

const InternalAutopilotCuratedSearch = ({
  onboarding = false,
}: {
  onboarding?: boolean;
}) => {
  const isCapacityLimitEnabled = useFeatureFlagEnabled(
    "internal-autopilot-capacity-limit",
  );

  const {
    prompt: selectedPrompt,
    setPrompt: setSelectedPrompt,
    savedFilters,
    setSavedFilters,
    scoringCriteriaValues,
    setScoringCriteriaValues,
    specificCompanies,
    expandPrompt,
    setSpecificCompanies,
    resetHydrationValues,
  } = useContext(InternalAutopilotHydrationContext);

  const [startedGeneratingFilters, setStartedGeneratingFilters] = useState(
    savedFilters !== null,
  );

  const {
    data: autopilotCuratedSearchData,
    loading: autopilotCuratedSearchLoading,
  } = useQuery<
    InternalAutopilotCuratedSearch_AutopilotCuratedSearchQuery,
    InternalAutopilotCuratedSearch_AutopilotCuratedSearchQueryVariables
  >(InternalAutopilotCuratedSearch_AutopilotCuratedSearch);

  const suggestedSearchPrompts = useMemo(() => {
    if (!autopilotCuratedSearchData?.personas) return;
    return autopilotCuratedSearchData.personas
      .filter((persona) => persona.source === EnumPersonaSource.Ciro)
      .slice(0, 3)
      .map((persona) => persona.description ?? "");
  }, [autopilotCuratedSearchData?.personas]);

  const reset = () => {
    resetHydrationValues({});
    setStartedGeneratingFilters(false);
  };

  if (isCapacityLimitEnabled) {
    return <CapacityLimitMessage />;
  }

  return (
    <AutopilotProvider>
      <AutopilotCuratedSearchContext.Provider
        value={{
          searchType: AutopilotCuratedSearchTypeEnum.Internal,
          selectedPrompt: selectedPrompt,
          setSelectedPrompt: (prompt) => {
            setSelectedPrompt(prompt || "");
          },
          specificCompanies,
          setSpecificCompanies,
          startedGeneratingFilters,
          setStartedGeneratingFilters,
          upsertAutopilotCuratedSearchLoading: false,
          remixPrompt: "",
          setRemixPrompt: (_prompt) => {
            throw new Error("Not implemented");
          },
          remixPromptOptions: [],
          setStartedRemixingFilters: () => {
            throw new Error("Not implemented");
          },
          expandScoringCriteriaValues: [],
          setExpandScoringCriteriaValues: () => {
            throw new Error("Not implemented");
          },
          setShowRemixStep: () => {
            throw new Error("Not implemented");
          },
          resetRemix: () => {
            throw new Error("Not implemented");
          },
          reset: () => {
            reset();
          },
          scoringCriteriaValues,
          setScoringCriteriaValues,
        }}
      >
        <AutopilotCuratedInternalSearchContext.Provider
          value={{
            onSaveFilters: async ({
              currentFilters,
              scoringCriteriaValues,
            }) => {
              setSavedFilters(currentFilters);
              setScoringCriteriaValues(scoringCriteriaValues);
            },
            savedFilters,
            setSavedFilters,
          }}
        >
          <CiroContainer className={classNames("bg-zinc-100")}>
            <CiroBreadCrumbs
              values={[
                "Autopilot",
                onboarding ? "Start searching" : "Search Ciro Database",
              ]}
              href="/autopilot"
            />
            <CuratedSearchBuilderHeading
              selectedPrompt={selectedPrompt}
              expandPrompt={expandPrompt}
              startedGeneratingFilters={startedGeneratingFilters}
              reset={reset}
              title="Search Ciro Database"
            />

            <CuratedSearchCriteriaAndFiltersContainer
              selectedPrompt={selectedPrompt}
              expandPrompt={expandPrompt}
              setSelectedPrompt={setSelectedPrompt}
              startedGeneratingFilters={startedGeneratingFilters}
              setStartedGeneratingFilters={setStartedGeneratingFilters}
              loading={autopilotCuratedSearchLoading}
              suggestedPrompts={suggestedSearchPrompts}
            />
          </CiroContainer>
        </AutopilotCuratedInternalSearchContext.Provider>
      </AutopilotCuratedSearchContext.Provider>
    </AutopilotProvider>
  );
};

export default InternalAutopilotCuratedSearch;
